import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';
import { Record } from './types';
import { RecordItem } from './RecordItem';

const DEFAULT_ITEMS_TO_SHOW = 3;

interface AccordionRecordSectionProps {
  records: Record[];
  maxItems?: number;
  showViewAll?: boolean;
  sectionTitle: string;
  onViewAll?: (sectionTitle: string) => void;
  onRecordClick?: (record: Record) => void;
  className?: string;
}

const AccordionRecordSection: React.FC<AccordionRecordSectionProps> = ({
  records,
  maxItems = DEFAULT_ITEMS_TO_SHOW,
  showViewAll = true,
  sectionTitle,
  onViewAll,
  onRecordClick,
  className = '',
}) => {
  const visibleRecords = records.slice(0, maxItems);
  const hasMoreRecords = records.length > maxItems;

  const handleViewAll = () => {
    onViewAll?.(sectionTitle);
  };

  const handleViewAllKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleViewAll();
    }
  };

  return (
    <div className={cn('px-6 pb-4', className)}>
      <div className="space-y-3">
        {visibleRecords.length > 0 ? (
          visibleRecords.map((record) => (
            <RecordItem key={record.id} record={record} onClick={onRecordClick} />
          ))
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">No records found.</p>
        )}
      </div>

      {showViewAll && hasMoreRecords && (
        <div className="mt-4 text-center">
          <Button
            variant="link"
            size="sm"
            className="text-sky-600 hover:text-sky-800 font-semibold"
            onClick={handleViewAll}
            onKeyDown={handleViewAllKeyDown}
          >
            View all ({records.length}) <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

AccordionRecordSection.displayName = 'AccordionRecordSection';

export { AccordionRecordSection };
